import React, { useCallback, useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Car, Star, Pencil, Trash2, Plus, X } from "lucide-react";

/**
 * Driver Garage — a driver's saved vehicles (drivers often own several cars).
 * Used by BOTH the dispatcher's driver drawer and the driver's own portal.
 * The primary vehicle is auto-applied when the driver is assigned to a trip;
 * dispatch can still pick a different car per trip on the reservation page.
 */
async function compressImage(file, { maxWidth = 720, quality = 0.78 } = {}) {
  const buf = await file.arrayBuffer();
  const url = URL.createObjectURL(new Blob([buf], { type: file.type }));
  try {
    const img = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = rej;
      im.src = url;
    });
    const w = Math.min(maxWidth, img.naturalWidth);
    const h = Math.round(img.naturalHeight * (w / img.naturalWidth));
    const cv = document.createElement("canvas");
    cv.width = w; cv.height = h;
    cv.getContext("2d").drawImage(img, 0, 0, w, h);
    return cv.toDataURL("image/jpeg", quality);
  } finally { URL.revokeObjectURL(url); }
}

const EMPTY = { year: "", make: "", model: "", color: "", plate: "", capacity: "" };

export function DriverGarage({ driverId, light = false }) {
  const [vehicles, setVehicles] = useState(null);
  const [editing, setEditing] = useState(null);   // null | "new" | vid
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);       // new data URL, "" = clear, null = unchanged
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/drivers/${driverId}/vehicles`);
      setVehicles(r.data || []);
    } catch { setVehicles([]); }
  }, [driverId]);
  useEffect(() => { load(); }, [load]);

  const startAdd = () => { setEditing("new"); setForm(EMPTY); setPhoto(null); };
  const startEdit = (v) => {
    setEditing(v.vid);
    setForm({ year: v.year || "", make: v.make || "", model: v.model || "", color: v.color || "", plate: v.plate || "", capacity: v.capacity ?? "" });
    setPhoto(null);
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); setPhoto(null); };

  const pickPhoto = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setPhotoBusy(true);
    try { setPhoto(await compressImage(file)); }
    catch { toast.error("Could not read that image"); }
    finally { setPhotoBusy(false); }
  };

  const save = async () => {
    if (!form.make.trim() && !form.model.trim()) { toast.error("Enter at least the make or model"); return; }
    setBusy(true);
    const payload = {
      year: form.year.trim() || null, make: form.make.trim() || null, model: form.model.trim() || null,
      color: form.color.trim() || null, plate: form.plate.trim() || null,
      capacity: form.capacity === "" ? null : Number(form.capacity),
    };
    if (photo !== null) payload.photo = photo === "" ? null : photo;
    try {
      if (editing === "new") await api.post(`/drivers/${driverId}/vehicles`, payload);
      else await api.patch(`/drivers/${driverId}/vehicles/${editing}`, payload);
      toast.success(editing === "new" ? "Vehicle added" : "Vehicle updated");
      cancel(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Remove ${[v.year, v.make, v.model].filter(Boolean).join(" ")}?`)) return;
    try { await api.delete(`/drivers/${driverId}/vehicles/${v.vid}`); toast.success("Vehicle removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const makePrimary = async (v) => {
    try { await api.post(`/drivers/${driverId}/vehicles/${v.vid}/primary`); toast.success(`${[v.make, v.model].filter(Boolean).join(" ")} is now the primary vehicle`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const tone = light
    ? { card: "border-neutral-200 bg-white", text: "text-neutral-900", sub: "text-neutral-500", input: "bg-white border-neutral-300 text-neutral-900" }
    : { card: "border-white/10 bg-[#0A0A0A]", text: "text-[#F5F2EB]", sub: "text-[#71717A]", input: "bg-[#0a0a0a] border-white/10 text-[#F5F2EB]" };

  if (vehicles === null) return <div className={`text-[10px] uppercase tracking-[0.18em] ${tone.sub}`}>Loading vehicles…</div>;

  return (
    <div data-testid="driver-garage" className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] flex items-center gap-2">
          <Car size={13}/> Vehicles ({vehicles.length})
        </div>
        {editing === null && (
          <button data-testid="garage-add-btn" onClick={startAdd}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10">
            <Plus size={11}/> Add vehicle
          </button>
        )}
      </div>

      {vehicles.length === 0 && editing === null && (
        <div className={`text-xs ${tone.sub}`} data-testid="garage-empty">No vehicles on file yet.</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {vehicles.map(v => (
          <div key={v.vid} data-testid={`garage-vehicle-${v.vid}`}
            className={`border p-3 flex gap-3 ${v.is_primary ? "border-[#D4AF37]/60" : tone.card}`}>
            <div className={`w-20 h-14 shrink-0 overflow-hidden border ${tone.card} flex items-center justify-center`}>
              {v.has_photo
                ? <img src={`${API}/drivers/${driverId}/vehicles/${v.vid}/photo`} alt="" className="w-full h-full object-cover"/>
                : <Car size={18} className={tone.sub}/>}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-sm truncate ${tone.text}`}>
                {[v.year, v.make, v.model].filter(Boolean).join(" ") || "Vehicle"}{v.color ? ` · ${v.color}` : ""}
              </div>
              <div className={`text-[10px] ${tone.sub}`}>
                {[v.plate && `Plate ${v.plate}`, v.capacity && `${v.capacity} pax`].filter(Boolean).join(" · ") || "—"}
              </div>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {v.is_primary ? (
                  <span data-testid={`garage-primary-badge-${v.vid}`} className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-[#D4AF37]"><Star size={9} fill="currentColor"/> Primary</span>
                ) : (
                  <button data-testid={`garage-set-primary-${v.vid}`} onClick={() => makePrimary(v)}
                    className={`text-[9px] uppercase tracking-[0.18em] ${tone.sub} hover:text-[#D4AF37]`}>Set primary</button>
                )}
                <button data-testid={`garage-edit-${v.vid}`} onClick={() => startEdit(v)}
                  className={`text-[9px] uppercase tracking-[0.18em] ${tone.sub} hover:text-[#D4AF37] inline-flex items-center gap-1`}><Pencil size={9}/> Edit</button>
                <button data-testid={`garage-delete-${v.vid}`} onClick={() => remove(v)}
                  className={`text-[9px] uppercase tracking-[0.18em] ${tone.sub} hover:text-red-400 inline-flex items-center gap-1`}><Trash2 size={9}/> Remove</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <div data-testid="garage-form" className={`border ${tone.card} p-4 space-y-3`}>
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">{editing === "new" ? "Add vehicle" : "Edit vehicle"}</div>
            <button onClick={cancel} className={`${tone.sub} hover:text-red-400`}><X size={14}/></button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[["year", "Year", "2026"], ["make", "Make", "GMC"], ["model", "Model", "Yukon"],
              ["color", "Color", "Black"], ["plate", "License plate", "ABC-1234"], ["capacity", "Passenger capacity", "6"]].map(([k, label, ph]) => (
              <label key={k} className="block">
                <span className={`text-[9px] uppercase tracking-[0.18em] ${tone.sub} block mb-1`}>{label}</span>
                <input data-testid={`garage-form-${k}`} value={form[k]} placeholder={ph}
                  type={k === "capacity" ? "number" : "text"}
                  onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                  className={`w-full border px-2.5 py-2 text-sm outline-none focus:border-[#D4AF37] ${tone.input}`}/>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {photo && <img src={photo} alt="" className="w-20 h-14 object-cover border border-white/10"/>}
            <label data-testid="garage-photo-pick" className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10">
              {photoBusy ? "Compressing…" : photo ? "Replace photo" : "Add photo (optional)"}
              <input type="file" accept="image/*" className="hidden" onChange={e => pickPhoto(e.target.files?.[0])}/>
            </label>
            {editing !== "new" && (
              <button data-testid="garage-photo-clear" onClick={() => setPhoto("")}
                className={`text-[9px] uppercase tracking-[0.18em] ${tone.sub} hover:text-red-400`}>Clear photo</button>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <button data-testid="garage-form-cancel" onClick={cancel} disabled={busy}
              className={`px-4 py-2 text-[10px] uppercase tracking-[0.18em] border ${tone.card} ${tone.sub}`}>Cancel</button>
            <button data-testid="garage-form-save" onClick={save} disabled={busy || photoBusy}
              className="px-5 py-2 text-[10px] uppercase tracking-[0.18em] bg-[#D4AF37] text-[#0a0a0a] font-semibold disabled:opacity-40">
              {busy ? "Saving…" : "Save vehicle"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DriverGarage;
